"use client";

import classNames from "classnames/bind";
import { useEffect, useState } from "react";
import { ProjectList } from "../ProjectList/ProjectList";
import { Chip } from "../Chip/Chip";
import { ChipList } from "../ChipList/ChipList";
import styles from "./ProjectsBlock.module.css";
import { Toggle } from "../Toggle/Toggle";
import { ProjectGrid } from "../ProjectGrid/ProjectGrid";
import { useRouter } from "next/navigation";

const cx = classNames.bind(styles);

export const ProjectsBlock = ({
  projects,
  tags,
  projectNameLabel,
  yearLabel,
  locationLabel,
  chipListLabel,
  toggleLabel,
  initialSelectedLayout,
}: {
  projects: {
    title: string;
    year: string;
    location: string;
    tags: string[];
    url: string;
    thumbnailUrl: string;
    hotspot: { x: number; y: number };
  }[];
  tags: { title: string; id: string }[];
  projectNameLabel: string;
  yearLabel: string;
  locationLabel: string;
  chipListLabel?: string;
  toggleLabel?: string;
  initialSelectedLayout: "grid" | "list";
}) => {
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedLayout, setSelectedLayout] = useState<"grid" | "list">(initialSelectedLayout);
  const router = useRouter();

  const filteredProjects =
    selectedTags.length === 0
      ? projects
      : projects.filter((item) => item.tags.some((item) => selectedTags.includes(item)));

  /**
   * Keeps the "layout" query-string parameter in-sync with the `selectedLayout`
   * so that the layout is correctly retained when clicking "back" from a
   * project detail page.
   */
  useEffect(() => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set("layout", selectedLayout);
    router.replace(currentUrl.toString());
  }, [selectedLayout, router]);

  return (
    <div className={cx("container")}>
      <div className={cx("controls")}>
        <ChipList ariaLabel={chipListLabel}>
          {tags.map((tag, index) => (
            <Chip
              key={index}
              onClick={() =>
                setSelectedTags((current) => {
                  const isSelected = current.includes(tag.id);
                  return isSelected
                    ? current.filter((item) => item !== tag.id)
                    : // Array.from(new Set()) is a de-duplication mechanism
                      Array.from(new Set([...current, tag.id]));
                })
              }
              active={selectedTags.includes(tag.id)}
            >
              {tag.title}
            </Chip>
          ))}
        </ChipList>
        <Toggle
          className={cx("toggle")}
          items={[
            { value: "grid", label: "Grid" },
            { value: "list", label: "List" },
          ]}
          selectedItem={selectedLayout}
          onClickItem={(clickedLayout) => setSelectedLayout(clickedLayout)}
          ariaLabel={toggleLabel}
        />
      </div>
      {selectedLayout === "list" && (
        <ProjectList
          projectNameLabel={projectNameLabel}
          yearLabel={yearLabel}
          locationLabel={locationLabel}
          items={filteredProjects}
        />
      )}
      {selectedLayout === "grid" && <ProjectGrid items={filteredProjects} />}
    </div>
  );
};
